import React, { useEffect, useRef, useState } from 'react'
import UploadProgress from '../uploadAndRecording/UploadProgress';
import { CircularProgress } from '@mui/material';
import { Mic, Square } from 'lucide-react';
import MicRecorder from "mic-recorder-to-mp3";
import useUserStore from '../../store/useUserStore';
import NoSleep from 'nosleep.js';


const Mp3Recorder = new MicRecorder({
    bitRate: 64,
    prefix: "data:audio/wav;base64,",
});

export const RecordingSurgicalNote = ({
    patientDetail,
    setActiveComponent,
    patientId,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [showProgressComponent, setShowProgressComponent] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    const {user, setIsRecordingStarted,fetchSurgicalData } = useUserStore();
    const fileInputRef = useRef(null);
    const noSleep = useRef(new NoSleep());
    const timerRef = useRef(null);
    const retryCountRef = useRef(0);
    const dentistId = user?.dentist_id;


    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const startRecording = async () => {
        setRecordingTime(0);
        if (!patientId) {
            alert("Please Enter Patient Details");
            return;
        }
        
        console.log("Recording started");

        try {
            // Then start recording
            await Mp3Recorder.start();
            setIsRecording(true);
            setIsRecordingStarted(true);
            noSleep.current.enable();

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);

        } catch (e) {
            console.error("Recording start failed:", e);
            noSleep.current.disable();
            setIsRecording(false);
            setIsRecordingStarted(false);
        }
    };

    const stopRecording = async () => {
        clearInterval(timerRef.current);
        try {
            const [buffer, blob] = await Mp3Recorder.stop().getMp3()
            console.log(blob);
            setIsRecordingStarted(false);
            setIsRecording(false);
            setShowProgressComponent(true);
            console.log("Recording stopped");
            uploadAndGenerateSurgicalNote(blob);
        } catch (error) {
            noSleep.current.disable();
            console.error("Error in recording:", error)
        }


    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const uploadAndGenerateSurgicalNote = async (blob) => {
        noSleep.current.enable();
        console.log(blob);
        let blob_name;
        if (blob) {
            const fName = `recording_${new Date().toISOString()}.mp3`;
            const formData = new FormData();
            formData.append("file", new File([blob], fName, { type: "audio/mp3" }));
            formData.append("patient_name", patientDetail.name.replace(/\s+/g, "_"));            
            try {
                blob_name = await uploadAudioFile(formData);
                console.log("Recording uploaded successfully", blob_name);
            } catch (error) {
                noSleep.current.disable()
                console.error("Error uploading recording:", error);
                throw error;
            }
        }

        const filename = blob_name || "";
        console.log(filename);
        let surgical_data;

        try {
            const noteData = {
                dentist_id: dentistId,
                patient_id: patientId,
                recording_fileName: filename,
                transcriptedFileName: "",
                surgical_note: ""
            }

            surgical_data =await createSurgicalData(noteData)
            console.log('Surgical note created:', surgical_data);
            setActiveComponent("upload-success")
        } catch (error) {
            noSleep.current.disable()
            console.error("Error in creating surgical data:", error);
            throw error;
        }


        try {
            await generateSurgicalNote(filename,surgical_data?.surgical_id);
            
        } catch (error) {
            noSleep.current.disable();
            console.error("Error saving transcript:", error);
        }
        noSleep.current.disable();
        fetchSurgicalData(dentistId)
        setActiveComponent("transcript-processing")
    };

    const uploadAudioFile = async (formData, isRetry = false) => {
        if (!isRetry) {
            retryCountRef.current = 0;
        }
        setUploadProgress(0);
        setIsProcessing(false);
        setProcessingProgress(0);
        console.log("uploading chunk with attempt:", retryCountRef.current);
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round(
                        (event.loaded / event.total) * 100
                    );
                    setUploadProgress(percentComplete);
                }
            });

            xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const responseData = JSON.parse(xhr.responseText);
                        setUploadProgress(100);
                        resolve(responseData.blob_name);
                    } catch (error) {
                        reject(error);
                    }
                } else {
                    if (retryCountRef.current < 2) {
                        retryCountRef.current += 1;
                        setTimeout(async () => {
                            await uploadAudioFile(formData, true).catch((error) => {
                                reject(error);
                            });
                        }, 2000);
                    } else {
                        reject(new Error(xhr.statusText));
                    }
                }
            });

            xhr.addEventListener("error", (error) => {
                if (retryCountRef.current < 2) {
                    retryCountRef.current += 1;
                    setTimeout(async () => {
                        await uploadAudioFile(formData, true).catch((error) => {
                            reject(error);
                        });
                    }, 2000);
                } else {
                    reject(error);
                }
            });

            xhr.addEventListener("timeout", () => {
                if (retryCountRef.current < 2) {
                    retryCountRef.current += 1;
                    setTimeout(async () => {
                        await uploadAudioFile(formData, true).catch((error) => {
                            reject(error);
                        });
                    }, 2000);
                } else {
                    reject(new Error("Upload timed out"));
                }
            });

            xhr.open(
                "POST",
                `${process.env.REACT_APP_SERVER_URL}/recordings/upload`,
                true
            );
            xhr.setRequestHeader("accept", "application/json");
            xhr.send(formData);
        });
    };

    const createSurgicalData = async (noteData) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/surgical-note`, {
                method: 'POST',
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(noteData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error creating surgical note:', error);
            throw error;
        }
    };


    const generateSurgicalNote = async (filename, surgical_id) => {
        console.log(filename);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SUMMARY_SERVER_URL}/surgical-note`,
                {
                    method: "POST",
                    headers: {
                        accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        filename,
                        surgical_id: surgical_id,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log("Generating surgical note :", data);
            return data;
        } catch (error) {
            console.error("Error generating surgical note:", error);
            throw error;
        }
    };


    return (
        <>
            <div className="recording-section">
                {showProgressComponent ? (
                    <UploadProgress
                        progress={uploadProgress}
                        isProcessing={isProcessing}
                        processingProgress={processingProgress}
                    />
                ) : !isLoading ? (
                    <>
                        {!isRecording ? (
                            <div className="aint">
                                <button className="record-btn" onClick={startRecording}>
                                    <Mic className="record-icon" />
                                    Start Recording
                                </button>
                                <h3 className="recording-title">Record session</h3>
                            </div>
                        ) : (
                            <div className="recording-active">
                                <div className="recording-buttons">
                                    <button
                                        className="stop-recording-btn"
                                        onClick={stopRecording}
                                    >
                                        <Square className="stop-icon" size={16} />
                                        Stop Recording
                                    </button>
                                </div>
                                <div className="recording-status">
                                    <div className="timer-container">
                                        <Mic className="mic-icon" />
                                        <span className="timer">{formatTime(recordingTime)}</span>
                                    </div>
                                </div>
                                <div className="recording-message">
                                    <h3>Recording....</h3>
                                    <p>Stay on this screen while conversing with your patient</p>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <CircularProgress />
                )}
            </div>
        </>
    )
}
